import { isNugget, normalizeNugget, type Nugget } from "@/features/nuggets/model/nuggetsSlice";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type AppNuggetFeedTimeframe =
  | "last_1_day"
  | "last_7_days"
  | "last_1_month"
  | "last_calendar_week"
  | "last_calendar_month";

const APP_NUGGET_FEED_TIMEFRAMES: readonly AppNuggetFeedTimeframe[] = [
  "last_1_day",
  "last_7_days",
  "last_1_month",
  "last_calendar_week",
  "last_calendar_month",
];

export function isAppNuggetFeedTimeframe(value: unknown): value is AppNuggetFeedTimeframe {
  return (
    typeof value === "string" &&
    (APP_NUGGET_FEED_TIMEFRAMES as readonly string[]).includes(value)
  );
}

export type AppNuggetFeedSummary = {
  id: number;
  app_id: number;
  tab_id: number | null;
  timeframe: AppNuggetFeedTimeframe;
  matched_start_date: string;
  matched_end_date: string;
  column_span: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type AppNuggetFeedDataItem = {
  id: number;
  app_id: number;
  tab_id: number | null;
  timeframe: AppNuggetFeedTimeframe;
  matched_start_date: string;
  matched_end_date: string;
  column_span: number;
  sort_order: number;
  nuggets: Nugget[];
};

function isAppNuggetFeedSummary(value: unknown): value is AppNuggetFeedSummary {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.app_id === "number" &&
    (row.tab_id === null || typeof row.tab_id === "number") &&
    isAppNuggetFeedTimeframe(row.timeframe) &&
    typeof row.matched_start_date === "string" &&
    typeof row.matched_end_date === "string" &&
    typeof row.column_span === "number" &&
    typeof row.sort_order === "number" &&
    typeof row.created_at === "string" &&
    typeof row.updated_at === "string"
  );
}

export function isAppNuggetFeedDataItem(value: unknown): value is AppNuggetFeedDataItem {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.app_id === "number" &&
    (row.tab_id === null || typeof row.tab_id === "number") &&
    isAppNuggetFeedTimeframe(row.timeframe) &&
    typeof row.matched_start_date === "string" &&
    typeof row.matched_end_date === "string" &&
    typeof row.column_span === "number" &&
    typeof row.sort_order === "number" &&
    Array.isArray(row.nuggets) &&
    row.nuggets.every(isNugget)
  );
}

export function normalizeAppNuggetFeedDataItem(item: AppNuggetFeedDataItem): AppNuggetFeedDataItem {
  return { ...item, nuggets: item.nuggets.map(normalizeNugget) };
}

export async function fetchAppNuggetFeeds(appId: number): Promise<AppNuggetFeedSummary[]> {
  const res = await apiFetch(`/v1/apps/${appId}/nugget-feeds`, { method: "GET" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!Array.isArray(data) || !data.every(isAppNuggetFeedSummary)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export type AddNuggetFeedToAppPayload = {
  tab_id?: number | null;
  timeframe: AppNuggetFeedTimeframe;
  column_span?: number;
};

export async function addNuggetFeedToApp(
  appId: number,
  payload: AddNuggetFeedToAppPayload,
): Promise<AppNuggetFeedSummary> {
  const res = await apiFetch(`/v1/apps/${appId}/nugget-feeds`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!isAppNuggetFeedSummary(data)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export type UpdateAppNuggetFeedPayload = {
  tab_id?: number | null;
  timeframe?: AppNuggetFeedTimeframe;
  column_span?: number;
};

export async function updateAppNuggetFeed(
  appId: number,
  feedId: number,
  payload: UpdateAppNuggetFeedPayload,
): Promise<AppNuggetFeedSummary> {
  const res = await apiFetch(`/v1/apps/${appId}/nugget-feeds/${feedId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!isAppNuggetFeedSummary(data)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function deleteAppNuggetFeed(appId: number, feedId: number): Promise<void> {
  const res = await apiFetch(`/v1/apps/${appId}/nugget-feeds/${feedId}`, { method: "DELETE" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
}

export async function removeNuggetFromFeed(
  appId: number,
  feedId: number,
  nuggetId: number,
): Promise<void> {
  const res = await apiFetch(`/v1/apps/${appId}/nugget-feeds/${feedId}/nuggets/${nuggetId}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
}
